import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import { formatDate } from '../../utils/helpers'

const STATUS_STYLES = {
  Draft: 'bg-gray-100 text-gray-700',
  'Pending Review': 'bg-amber-100 text-amber-700',
  'Pending Signature': 'bg-blue-100 text-blue-700',
  Active: 'bg-green-100 text-green-700',
  Terminated: 'bg-red-100 text-red-700',
  Expired: 'bg-gray-100 text-gray-500',
}

export default function ContractList() {
  const { t } = useTranslation('contracts')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [projectFilter, setProjectFilter] = useState('all')
  const [consultantFilter, setConsultantFilter] = useState('all')
  const [sortDir, setSortDir] = useState('newest')

  useEffect(() => {
    api.get('/contracts/')
      .then(r => setContracts(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load contracts'))
      .finally(() => setLoading(false))
  }, [])

  // Distinct project + consultant options (labelled with their IDs) for the filters.
  const projectOptions = useMemo(() => {
    const m = new Map()
    for (const c of contracts) if (c.project_id != null) {
      m.set(c.project_id, `${c.project_number || `#${c.project_id}`}${c.project_name ? ` · ${c.project_name}` : ''}`)
    }
    return [...m.entries()]
  }, [contracts])
  const consultantOptions = useMemo(() => {
    const m = new Map()
    for (const c of contracts) if (c.consultant_id != null) {
      m.set(c.consultant_id, `${c.consultant_name || `User #${c.consultant_id}`}${c.consultant_code ? ` · ${c.consultant_code}` : ''}`)
    }
    return [...m.entries()]
  }, [contracts])

  const visibleContracts = useMemo(() => {
    let list = contracts.filter(c =>
      (projectFilter === 'all' || String(c.project_id) === String(projectFilter)) &&
      (consultantFilter === 'all' || String(c.consultant_id) === String(consultantFilter))
    )
    const ts = (c) => new Date(c.effective_date || c.created_at || 0).getTime()
    list = [...list].sort((a, b) => sortDir === 'newest' ? ts(b) - ts(a) : ts(a) - ts(b))
    return list
  }, [contracts, projectFilter, consultantFilter, sortDir])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{visibleContracts.length} of {contracts.length} contract{contracts.length !== 1 ? 's' : ''}</p>
        </div>
        {/* Filters: by Project ID, by Consultant ID, + date sort */}
        <div className="flex flex-wrap items-center gap-2">
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="all">{t('detail2.all_projects')}</option>
            {projectOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={consultantFilter} onChange={e => setConsultantFilter(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="all">{t('detail2.all_consultants')}</option>
            {consultantOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={sortDir} onChange={e => setSortDir(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="newest">{t('detail2.newest_first')}</option>
            <option value="oldest">{t('detail2.oldest_first')}</option>
          </select>
        </div>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-4">📄</div>
          <p className="text-sm">{t('detail2.no_contracts')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('detail2.contract_hash')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('list.project')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('list.consultant')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('list.status')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('list.total_fee')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{t('list.effective_date')}</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">{tc('labels.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleContracts.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/contracts/${c.id}`)}
                >
                  <td className="px-5 py-3 font-mono text-blue-700">{c.contract_number || `#${c.id}`}</td>
                  <td className="px-5 py-3 text-gray-700">{c.project_number || `Project #${c.project_id}`}{c.project_name ? <span className="text-gray-400"> · {c.project_name}</span> : null}</td>
                  <td className="px-5 py-3 text-gray-700">{c.consultant_name || (c.consultant_id ? `User #${c.consultant_id}` : '—')}{c.consultant_code ? <span className="text-gray-400 font-mono text-xs"> · {c.consultant_code}</span> : null}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-600'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {c.total_fee ? `$${Number(c.total_fee).toLocaleString()}` : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {c.effective_date ? formatDate(c.effective_date) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/contracts/${c.id}`) }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
